<template>
  <div>
    <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
      Select Competitors to Analyze
    </h3>

    <div class="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
      <p class="text-xs text-amber-800 dark:text-amber-300">
        Use real local business pages for benchmarks. Direct local businesses with matching location and keyword signals are selected by default; government listings, national organizations, directories, social profiles, manufacturer profiles, client-owned pages, and lead-gen pages are never selected automatically.
      </p>
    </div>

    <form class="mb-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50" @submit.prevent="addManualCompetitor">
      <label for="manual-competitor-url" class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
        Manual competitor URL
      </label>
      <div class="flex flex-col sm:flex-row gap-2">
        <input
          id="manual-competitor-url"
          v-model="manualCompetitorUrl"
          type="text"
          placeholder="https://competitor.com/page"
          class="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <button type="submit" class="btn btn-secondary btn-sm">
          Add Competitor
        </button>
      </div>
      <p v-if="manualError" class="mt-2 text-xs text-red-600 dark:text-red-400">
        {{ manualError }}
      </p>
    </form>

    <div v-if="manualSelectedUrls.length" class="mb-4 space-y-2">
      <div
        v-for="url in manualSelectedUrls"
        :key="url"
        class="flex items-center justify-between gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20">
        <div class="min-w-0">
          <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {{ extractDomain(url) }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 truncate">
            {{ url }}
          </div>
        </div>
        <button type="button" class="text-xs text-red-500 hover:text-red-600" @click="removeUrl(url)">
          Remove
        </button>
      </div>
    </div>

    <div v-if="organicResults.length === 0" class="text-center py-6">
      <p class="text-gray-500 dark:text-gray-400">
        No organic results available. Add competitor URLs manually.
      </p>
    </div>

    <div v-else class="space-y-3 mb-6">
      <label
        v-for="result in organicResults"
        :key="result.link"
        :class="[
          'flex items-start gap-3 p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer',
          selectedUrls.includes(result.link)
            ? 'border-primary-300 dark:border-primary-700 bg-primary-50/40 dark:bg-primary-900/10'
            : 'border-gray-200 dark:border-gray-700'
        ]">
        <input
          type="checkbox"
          :value="result.link"
          v-model="selectedUrls"
          class="mt-1 rounded border-gray-300">
        <div class="flex-1 min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ result.title }}</div>
            <span v-if="result.selection_recommended" class="px-2 py-0.5 rounded-lg text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
              Recommended
            </span>
            <span v-else class="px-2 py-0.5 rounded-lg text-[11px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
              Review
            </span>
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ result.link }}</div>
          <div class="flex flex-wrap gap-2 mt-2">
            <span class="px-2 py-0.5 rounded-lg text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              #{{ result.position || 'Manual' }}
            </span>
            <span :class="typeBadge(result.competitor_type)" class="px-2 py-0.5 rounded-lg text-[11px]" title="Result class">
              {{ result.competitor_type_label || 'Unclassified' }}
            </span>
            <span
              v-if="result.result_page_type_label"
              class="px-2 py-0.5 rounded-lg text-[11px] bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
              title="Page type (from URL)">
              {{ result.result_page_type_label }}
            </span>
            <span
              v-if="showLocationBadge(result)"
              :class="locationBadge(result.location_match)"
              class="px-2 py-0.5 rounded-lg text-[11px]">
              {{ result.location_match_label || 'Location Not Detected' }}
            </span>
            <span class="px-2 py-0.5 rounded-lg text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              Quality {{ result.quality_score || 0 }}/100
            </span>
          </div>
          <ul v-if="failedChecks(result).length" class="mt-2 space-y-0.5">
            <li
              v-for="check in failedChecks(result)"
              :key="`${result.link}-fail-${check.key}`"
              class="text-xs text-red-600 dark:text-red-400">
              {{ check.label }}: {{ check.detail }}
            </li>
          </ul>
          <div v-if="result.selection_reasoning?.length" class="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {{ result.selection_reasoning.join(' ') }}
          </div>
          <div v-if="result.quality_checks?.length" class="mt-2 flex flex-wrap gap-1">
            <span
              v-for="check in result.quality_checks"
              :key="`${result.link}-${check.key}`"
              :class="checkBadge(check.status)"
              class="px-1.5 py-0.5 rounded-lg text-[10px]">
              {{ check.label }}: {{ check.status === 'na' ? 'n/a' : check.status }}
            </span>
          </div>
        </div>
      </label>
    </div>

    <div class="flex items-center justify-between">
      <span class="text-sm text-gray-500">{{ selectedUrls.length }} competitors selected · minimum 3 recommended</span>
      <button
        @click="$emit('start', selectedUrls)"
        :disabled="selectedUrls.length === 0"
        class="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
        Start Analysis
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  serpAnalysis: { type: Object, required: true }
})

defineEmits(['start'])

const extractDomain = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url }
}

const organicResults = computed(() => props.serpAnalysis?.organic_results || props.serpAnalysis?.raw_serp_data?.organic_results || [])

const isDirect = (r) => r.competitor_type === 'direct_competitor'

// Only direct local businesses are ever pre-selected.
const defaultSelectedUrls = () => {
  const recommended = organicResults.value.filter((r) => r.selection_recommended && isDirect(r)).slice(0, 5)
  const fallback = organicResults.value.filter((r) => r.competitor_type === 'direct_competitor' && !recommended.includes(r))
  return [...recommended, ...fallback].slice(0, 5).map((r) => r.link)
}

const selectedUrls = ref(defaultSelectedUrls())
const manualCompetitorUrl = ref('')
const manualError = ref('')
const organicUrls = computed(() => new Set(organicResults.value.map((r) => r.link)))
const manualSelectedUrls = computed(() => selectedUrls.value.filter((url) => !organicUrls.value.has(url)))

watch(organicResults, () => {
  selectedUrls.value = defaultSelectedUrls()
})

const normalizeUrl = (url) => {
  let raw = url.trim()
  if (!raw) return null
  if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`

  try {
    const parsed = new URL(raw)
    parsed.hash = ''
    if (!parsed.pathname) parsed.pathname = '/'
    return parsed.toString()
  } catch {
    return null
  }
}

const addManualCompetitor = () => {
  manualError.value = ''
  const normalized = normalizeUrl(manualCompetitorUrl.value)
  if (!normalized) {
    manualError.value = 'Enter a valid competitor URL.'
    return
  }
  if (!selectedUrls.value.includes(normalized)) {
    selectedUrls.value.push(normalized)
  }
  manualCompetitorUrl.value = ''
}

const removeUrl = (url) => {
  selectedUrls.value = selectedUrls.value.filter((selectedUrl) => selectedUrl !== url)
}

// A city mention on a listing page is not a positive local signal; only show
// positive location badges for direct local businesses. Listings, directories,
// social profiles and lead-gen pages get no location badge at all (the
// classifier labels their location match "Not applicable").
const LISTING_TYPES = ['government_listing', 'national_organization', 'directory', 'social_profile', 'spam_lead_gen']
const isListing = (r) => LISTING_TYPES.includes(r.competitor_type)
const showLocationBadge = (r) => isDirect(r) || (r.location_match === 'mismatch' && !isListing(r))

const failedChecks = (r) => (r.quality_checks || []).filter((check) => check.status === 'fail' && check.detail)

const typeBadge = (type) => {
  const map = {
    direct_competitor: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    government_listing: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    national_organization: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    directory: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    social_profile: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    manufacturer_dealer_profile: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    spam_lead_gen: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    client_owned_profile: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
  }
  return map[type] || map.client_owned_profile
}

const locationBadge = (match) => {
  const map = {
    exact_city_match: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    nearby_city: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    statewide: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    mismatch: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    unknown: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
  }
  return map[match] || map.unknown
}

const checkBadge = (status) => {
  const map = {
    pass: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
    warn: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
    fail: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    na: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
  }
  return map[status] || map.warn
}
</script>
